<script setup lang="ts">
import type { EnrichedSearchResult } from '~/utils/solr/search'
import { faArrowRight, faLockKeyhole, faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons'
import {
  getSearchResultAccessibleLabel,
  getSearchResultTitleContext,
  shouldShowSeparateTitleContext,
  resolveSearchResult,
  truncateAuthors,
} from '~/utils/solr/search-utils'

const props = defineProps<{
  previewResults: EnrichedSearchResult[]
  contentTypeOptions: { value: string, label: string, disabled: boolean }[]
  toggleContentType: (type: string, checked: boolean, singleSelect?: boolean) => void
  selectedContentTypes: string[]
  resetContentType: () => void
  totalResults: number
  searchTerm: string
}>()

const emit = defineEmits(['select', 'focusInput'])

const hasLicense = useHasLicense()

const listRef = useTemplateRef('list')

const { onKeydown, focusFirst } = useListKeyboardNavigation(listRef, () => emit('focusInput'))

defineExpose({ focusFirst })

const SORT_CONTENT_TYPE_REGEX = /\((.*?)\)/

const sortedContentTypeOptions = computed(() => {
  return [...props.contentTypeOptions].sort((a, b) => {
    const countA = Number.parseInt(a.label.match(SORT_CONTENT_TYPE_REGEX)?.[1] || '0')
    const countB = Number.parseInt(b.label.match(SORT_CONTENT_TYPE_REGEX)?.[1] || '0')
    return countB - countA
  })
})

const totalFromFacets = computed(() => {
  return props.contentTypeOptions.reduce((sum, option) => {
    const count = Number.parseInt(option.label.match(SORT_CONTENT_TYPE_REGEX)?.[1] || '0')
    return sum + count
  }, 0)
})

const MAX_PREVIEW_AUTHORS = 3

function formatItemMeta(item: EnrichedSearchResult): string {
  const hasEditionNumber = item.editionNumber
  let date: string
  if (item.nodeType === 'book') {
    date = formatDate(item.publishedAt, { year: 'numeric' })
  }
  else {
    date = formatDate(item.publishedAt, { year: 'numeric', month: 'long', day: 'numeric' })
  }
  const editionDate = hasEditionNumber ? `${item.editionNumber}. utgave ${date}` : date
  return [editionDate, truncateAuthors(item.author, MAX_PREVIEW_AUTHORS)].filter(Boolean).join(' - ')
}

function formatItemTitleContext(item: EnrichedSearchResult): string {
  return shouldShowSeparateTitleContext(item)
    ? getSearchResultTitleContext(item).join(' \u2013 ')
    : ''
}
</script>

<template>
  <div class="flex flex-col gap-xs p-s">
    <div class="flex gap-xs items-center overflow-x-auto no-scrollbar">
      <Chip
        size="large"
        variant="neutral"
        color="carbon"
        aria-label="Alle"
        type="button"
        class="shrink-0 hover:bg-surface-hover"
        :class="{ 'bg-surface-primary': selectedContentTypes.length === 0 }"
        @click="resetContentType"
        @mousedown.prevent
      >
        Alle ({{ totalFromFacets }})
      </Chip>
      <Chip
        v-for="option in sortedContentTypeOptions"
        :key="option.value"
        size="large"
        variant="neutral"
        color="carbon"
        :aria-label="option.label"
        class="shrink-0 transition-colors duration-300 ease-in-out"
        type="button"
        :disabled="option.disabled"
        :class="[{ 'bg-surface-primary': selectedContentTypes.includes(option.value), 'hover:bg-surface-hover': !selectedContentTypes.includes(option.value) }]"
        @click="toggleContentType(option.value, !selectedContentTypes.includes(option.value), true)"
        @mousedown.prevent
      >
        {{ option.label }}
      </Chip>
    </div>
    <div class="flex flex-col gap-2xs">
      <span v-if="totalResults" class="text-caption">Viser 10 av {{ totalResults }} treff</span>
      <ul ref="list" aria-label="Søkeresultater" class="overflow-y-auto scrollbar-carbon max-h-[50vh]">
        <li v-for="(item, index) in previewResults" :key="index" class="flex items-center w-full">
          <NuxtLink
            :to="resolveSearchResult(item, hasLicense)"
            :aria-label="getSearchResultAccessibleLabel(item)"
            class="
              no-underline flex items-center w-full gap-xs py-xs pl-2xs pr-m
              border-b border-b-border-secondary
              hover:bg-surface-hover hover:rounded-lg hover:pb-calc(theme(spacing.xs)-1px) hover:border-b-transparent
              focus-visible:bg-surface-hover focus-visible:rounded-lg focus-visible:pb-calc(theme(spacing.xs)-1px) focus-visible:border-b-transparent focus-visible:outline-none
            "
            @mousedown.prevent
            @keydown="onKeydown"
            @click="$emit('select')"
          >
            <Icon :icon="hasLicense ? faMagnifyingGlass : faLockKeyhole" size="sm" />

            <div class="flex flex-col gap-3xs">
              <span class="preview-search-result" v-html="item.previewHighlightedTitle" />
              <span v-if="formatItemTitleContext(item)" class="text-caption text-secondary">
                {{ formatItemTitleContext(item) }}
              </span>
              <div class="flex items-center gap-3xs text-caption text-secondary">
                <template v-if="item.nodeType !== 'author'">
                  <span class="text-caption text-secondary">{{ formatItemMeta(item) }}</span>
                </template>
              </div>
            </div>
          </NuxtLink>
        </li>
      </ul>
    </div>
  </div>
  <Button
    v-if="totalFromFacets"
    variant="primary"
    color="moss"
    size="large"
    :aria-label="`Se alle ${totalFromFacets} treff for ${searchTerm}`"
    :to="{ name: 'search-index', query: { sq: searchTerm } }"
    class="w-full !justify-start gap-xs text-small bg-moss-500 hover:bg-moss-400 px-s !py-[20px] !text-secondary-on_dark rounded-none rounded-b-lg"
    @mousedown.prevent
    @click="$emit('select')"
  >
    <Icon :icon="faArrowRight" size="sm" />
    <span>Se alle {{ totalFromFacets }} treff for <span class="font-bold">{{ searchTerm }}</span></span>
  </Button>
</template>
